import React from 'react';
import SettingsSidebar from './SettingsSidebar';
import SettingsMobileChrome from './SettingsMobileChrome';
import AccountSettingsForm from './AccountSettingsForm';
import VenueDetailsForm from './VenueDetailsForm';
import OperatingHoursForm from './OperatingHoursForm';
import PricingControlsForm from './PricingControlsForm';
import PaymentSettingsForm from './PaymentSettingsForm';
import CalendarSyncSettings from './CalendarSyncSettings';
import SettingsSpaceScopeBar from './SettingsSpaceScopeBar';

const SettingsSection = ({
  activeSubSection,
  onSubSectionChange,
  accountSettings,
  setAccountSettings,
  ownerAccountSettings = {},
  isOwnerViewingOwnVenue = false,
  venueId,
  venueDetails,
  setVenueDetails,
  isAdmin = false,
  addOnItems,
  setAddOnItems,
  otherItems,
  setOtherItems,
  packages,
  setPackages,
  operatingHours,
  onOperatingHoursChange,
  operatingHoursPerPerson,
  onOperatingHoursPerPersonChange,
  holidays,
  onHolidayDelete,
  draftHolidays,
  onNewHolidayChange,
  addAnotherHoliday,
  removeDraftHoliday,
  defaultPrice,
  onDefaultPriceChange,
  defaultPerPersonPrice,
  onDefaultPerPersonPriceChange,
  dynamicPricing,
  onPricingDelete,
  dynamicPricingPerPerson,
  onPricingPerPersonDelete,
  draftDynamicPricing,
  onDraftPricingChange,
  addAnotherDynamicPricing,
  removeDraftPricing,
  draftDynamicPricingPerPerson,
  onDraftPricingPerPersonChange,
  addAnotherDynamicPricingPerPerson,
  removeDraftPricingPerPerson,
  discountCodes,
  onCreateDiscountCode,
  onDeleteDiscountCode,
  flatPricing,
  draftFlatPricing,
  onFlatPricingRowChange,
  onFlatPricingDelete,
  addDraftFlatPricing,
  removeDraftFlatPricing,
  bookingRules,
  onBookingRulesChange,
  onSave,
  onAccountSettingsSave,
  onNotify,
  onSectionChange,
  stripePaymentsEnabled = false,
  venueSpaces = [],
  settingsSpaceId = null,
  onSettingsSpaceIdChange,
  multiSpace = false,
  scopeLoading = false,
  ownerEmail = '',
  syncCalendar,
  syncLoading = false,
  pushCalendarToGoogle,
  pushLoading = false
}) => {
  const canShowPayments = Boolean(stripePaymentsEnabled);
  const showDisabledPaymentsMessage = activeSubSection === 'payments' && !canShowPayments;

  return (
    <div className="settings-section">
      <SettingsMobileChrome
        activeSubSection={activeSubSection}
        onSubSectionChange={onSubSectionChange}
        onSectionChange={onSectionChange}
        showPayments={canShowPayments}
      />
      <div className="settings-content">
        <SettingsSidebar 
          activeSubSection={activeSubSection}
          onSubSectionChange={onSubSectionChange}
          showPayments={canShowPayments}
        />
        
        <div
          className={`settings-main ${activeSubSection === 'pricing-controls' ? 'settings-main--pricing' : ''}`}
          style={{ width: '100%', maxWidth: 'none' }}
        >
          {activeSubSection !== 'account-settings' &&
            activeSubSection !== 'venue-details' &&
            activeSubSection !== 'payments' &&
            activeSubSection !== 'calendar-sync' && (
            <div
              className={`settings-actions ${activeSubSection === 'pricing-controls' ? 'settings-actions--pricing' : ''}`}
            >
              {activeSubSection === 'pricing-controls' && (
                <h3 className="settings-actions-title">Pricing controls</h3>
              )}
              <div className="settings-actions-buttons">
                <button className="btn-secondary">
                  {activeSubSection === 'pricing-controls' ? 'Cancel' : 'Reset'}
                </button>
                <button
                  className="btn-primary"
                  onClick={() => onSave(activeSubSection)}
                >
                  Save
                </button>
              </div>
            </div>
          )}
          
          {activeSubSection === 'account-settings' && (
            <AccountSettingsForm
              accountSettings={accountSettings}
              setAccountSettings={setAccountSettings}
              ownerAccountSettings={ownerAccountSettings}
              isOwnerViewingOwnVenue={isOwnerViewingOwnVenue}
              onSave={onAccountSettingsSave}
              onNotify={onNotify}
            />
          )}
          
          {activeSubSection === 'venue-details' && (
            <VenueDetailsForm
              venueId={venueId}
              venueDetails={venueDetails}
              setVenueDetails={setVenueDetails}
              addOnItems={addOnItems}
              setAddOnItems={setAddOnItems}
              otherItems={otherItems}
              setOtherItems={setOtherItems}
              packages={packages}
              setPackages={setPackages}
              isAdmin={isAdmin}
              onSaveVenueDetails={(patch) => onSave('venue-details', patch)}
              onNotify={onNotify}
              venueSpaces={venueSpaces}
              settingsSpaceId={settingsSpaceId}
              onSettingsSpaceIdChange={onSettingsSpaceIdChange}
              multiSpace={multiSpace}
            />
          )}
          
          {activeSubSection === 'calendar-sync' && (
            <div className="settings-panel">
              {multiSpace ? (
                <SettingsSpaceScopeBar
                  spaces={venueSpaces}
                  value={settingsSpaceId}
                  onChange={onSettingsSpaceIdChange}
                  showVenueWide={false}
                  minSpacesToShow={1}
                  ariaLabel="Select space for calendar mapping"
                />
              ) : null}
              <CalendarSyncSettings
                venueId={venueId}
                venueDetails={venueDetails}
                settingsSpaces={venueSpaces}
                settingsSpaceId={settingsSpaceId}
                syncCalendar={syncCalendar}
                syncLoading={syncLoading}
                pushCalendarToGoogle={pushCalendarToGoogle}
                pushLoading={pushLoading}
                onNotify={onNotify}
              />
            </div>
          )}

          {activeSubSection === 'operating-hours' && (
            <OperatingHoursForm
              operatingHours={operatingHours}
              onOperatingHoursChange={onOperatingHoursChange}
              operatingHoursPerPerson={operatingHoursPerPerson}
              onOperatingHoursPerPersonChange={onOperatingHoursPerPersonChange}
              holidays={holidays}
              onHolidayDelete={onHolidayDelete}
              draftHolidays={draftHolidays}
              onNewHolidayChange={onNewHolidayChange}
              addAnotherHoliday={addAnotherHoliday}
              removeDraftHoliday={removeDraftHoliday}
              venueId={venueId}
              venueSpaces={venueSpaces}
              settingsSpaceId={settingsSpaceId}
              onSettingsSpaceIdChange={onSettingsSpaceIdChange}
              multiSpace={multiSpace}
              scopeLoading={scopeLoading}
              ownerEmail={ownerEmail}
              onNotify={(type, title, message) =>
                onNotify?.({ type, title, message: message || '' })
              }
            />
          )}
          
          {activeSubSection === 'pricing-controls' && (
            <PricingControlsForm
              defaultPrice={defaultPrice}
              onDefaultPriceChange={onDefaultPriceChange}
              defaultPerPersonPrice={defaultPerPersonPrice}
              onDefaultPerPersonPriceChange={onDefaultPerPersonPriceChange}
              dynamicPricing={dynamicPricing}
              onPricingDelete={onPricingDelete}
              dynamicPricingPerPerson={dynamicPricingPerPerson}
              onPricingPerPersonDelete={onPricingPerPersonDelete}
              draftDynamicPricing={draftDynamicPricing}
              onDraftPricingChange={onDraftPricingChange}
              addAnotherDynamicPricing={addAnotherDynamicPricing}
              removeDraftPricing={removeDraftPricing}
              draftDynamicPricingPerPerson={draftDynamicPricingPerPerson}
              onDraftPricingPerPersonChange={onDraftPricingPerPersonChange}
              addAnotherDynamicPricingPerPerson={addAnotherDynamicPricingPerPerson}
              removeDraftPricingPerPerson={removeDraftPricingPerPerson}
              discountCodes={discountCodes}
              onCreateDiscountCode={onCreateDiscountCode}
              onDeleteDiscountCode={onDeleteDiscountCode}
              onNotify={onNotify}
              flatPricing={flatPricing}
              draftFlatPricing={draftFlatPricing}
              onFlatPricingDelete={onFlatPricingDelete}
              addDraftFlatPricing={addDraftFlatPricing}
              removeDraftFlatPricing={removeDraftFlatPricing}
              onFlatPricingRowChange={onFlatPricingRowChange}
              bookingRules={bookingRules}
              onBookingRulesChange={onBookingRulesChange}
              venueSpaces={venueSpaces}
              settingsSpaceId={settingsSpaceId}
              onSettingsSpaceIdChange={onSettingsSpaceIdChange}
              multiSpace={multiSpace}
              scopeLoading={scopeLoading}
            />
          )}

          {activeSubSection === 'payments' && canShowPayments && (
            <PaymentSettingsForm venueId={venueId} venueDetails={venueDetails} />
          )}
          {showDisabledPaymentsMessage && (
            <div className="settings-panel">
              <h3>Payments</h3>
              <p className="settings-description">
                Stripe payments are disabled for this venue. Ask an admin to enable the feature from the admin panel.
              </p>
              <button className="btn-secondary" onClick={() => onSubSectionChange('pricing-controls')}>
                Back to Pricing controls
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsSection;

